import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Search, FolderKanban, BookTemplate, FileText, Newspaper, Plus, Upload, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const TYPE_ICONS = {
  project: FolderKanban, template: BookTemplate,
  guidance: FileText, blog: Newspaper,
};
const TYPE_COLORS = {
  project: 'text-primary', template: 'text-violet-400',
  guidance: 'text-sky-400', blog: 'text-amber-400',
};

const QUICK_ACTIONS = [
  { title: 'Create Project', url: '/app/projects', icon: Plus },
  { title: 'Browse Templates', url: '/app/templates', icon: BookTemplate },
  { title: 'Search Guidances', url: '/app/guidances', icon: FileText },
];

export default function CommandPalette({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/search`, { params: { q, limit: 8 } });
      setResults(res.data.results || []);
      setSelected(0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (item) => {
    onClose();
    navigate(item.url);
  };

  const handleKeyDown = (e) => {
    const items = query.length < 2 ? QUICK_ACTIONS : results;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' && items[selected]) { handleSelect(items[selected]); }
    else if (e.key === 'Escape') { onClose(); }
  };

  const items = query.length < 2 ? QUICK_ACTIONS : results;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Search projects, templates, guidances..."
            className="border-0 bg-transparent h-8 p-0 focus-visible:ring-0 text-sm" />
          <kbd className="text-[9px] font-mono text-muted-foreground/50 bg-muted px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-[300px] overflow-y-auto py-1">
          {query.length < 2 && (
            <div className="px-3 py-1.5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 mb-1">Quick Actions</p>
            </div>
          )}
          {query.length >= 2 && results.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No results found</div>
          )}
          {items.map((item, i) => {
            const Icon = TYPE_ICONS[item.type] || item.icon || FileText;
            const color = TYPE_COLORS[item.type] || 'text-muted-foreground';
            return (
              <button key={i} onClick={() => handleSelect(item)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors ${
                  i === selected ? 'bg-muted/50' : ''
                }`}>
                <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.title}</p>
                  {item.subtitle && <p className="text-[10px] text-muted-foreground truncate">{item.subtitle}</p>}
                </div>
                {item.type && (
                  <span className="text-[9px] font-mono text-muted-foreground/50 capitalize">{item.type}</span>
                )}
                <ArrowRight className="w-3 h-3 text-muted-foreground/30" />
              </button>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-border flex items-center justify-between">
          <div className="flex gap-3 text-[9px] text-muted-foreground/50">
            <span>↑↓ Navigate</span>
            <span>↵ Open</span>
            <span>ESC Close</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
